import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { GapRepresentativeValue } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'

import { AREA_CODE_OPTIONS } from '~/models/area'
import { now as serverNow } from '~/models/clock'
import { startOfTodayJst } from '~/models/dateTime'
import {
  calendarDateString,
  defineFilterCodec,
  guarded,
  listOf,
  oneOf,
} from '~/models/filterCodec'
import { REP_VALUE_OPTIONS } from '~/pages/SupplyGap/models/filterOptions'
import { isBreakdownMode } from '~/pages/SupplyGap/models/supplyCategories'
import {
  type GapFilterState,
  isRangeType,
  parseYmd,
  snapToMonthStart,
  snapToSaturday,
  toYmd,
} from '~/pages/SupplyGap/models/types'

/**
 * 需給ギャップのフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`)「フィルタバーを持つ全画面
 * において、 フィルタの選択状態を localStorage で永続化」。 供給内訳トグルの保存先も
 * これに従う (spec `supply-demand-gap.md` AC-SDG-004c の本文は「サーバー側に保存」 だが、
 * 後から決まった横断 AC-SC-031 が全画面のフィルタ保持先を localStorage に統一している)。
 *
 * 保存 JSON の**キー名は変えない**
 * (既存利用者の保存値をそのまま読める = フィルタが一度だけ既定に戻る事故を起こさない)。
 * 後から増えた項目は欠落を既定値へ落として後方互換にする。
 */
export const SUPPLY_GAP_FILTERS_STORAGE_KEY = 'ebalance-supply-gap-filters'

/** 週間モードの週数候補。 UI の選択肢と復元時の検証を同じ集合にする。 */
export const WEEK_DUR_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10, 12] as const

/** 月間モードの月数候補。 UI の選択肢と復元時の検証を同じ集合にする。 */
export const MONTH_DUR_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10, 12] as const

/**
 * フィルタ既定値。
 *
 * `startOfTodayJst` は「JST の今日」をカレンダー日 `Date` (= ローカル 0:00) で
 * 返すので、 後段の `toYmd` / `snapToSaturday` / `snapToMonthStart` はローカル
 * getter のままで対称になる。
 *
 * `now` を注入可能にするのは 2 つの時計の混在を避けるため。
 */
export function defaultSupplyGapFilters(
  now: Date = serverNow(),
): GapFilterState {
  const today = startOfTodayJst(now)
  return {
    rangeType: 'day',
    dayDate: toYmd(today),
    weekStart: toYmd(snapToSaturday(today)),
    weekDur: 3,
    monthStart: toYmd(snapToMonthStart(today)),
    monthDur: 3,
    representativeValue: GapRepresentativeValue.BASE,
    // AC-SDG-004c: 初期表示は「集約」。
    breakdownMode: 'aggregate',
    // 空 = 全業務エリア表示 (design `_initSel` 同様、 先頭 1 件に絞り込まない)。
    selectedAreaCodes: [],
  }
}

const ymd = calendarDateString({ parse: parseYmd, format: toYmd })

/**
 * 保存形式は `{ rangeType, dayDate, weekStart, weekDur, monthStart, monthDur,
 * representativeValue, breakdownMode, selectedAreaCodes }`。
 *
 * **項目ごとに独立して検証し、不正なものだけ既定へ落とす** (all-or-nothing にしない)。
 * 壊れた `weekDur` が `weekDur * 7` に流れて NaN の期間を BFF へ送る経路を作らない。
 *
 * 代表値は `UNSPECIFIED` (0) を**含めない** — RadioButton が未選択表示になるのに
 * body は BASE として問い合わせるため、 表示と実行が乖離する。
 *
 * **`businessAreaCodes` に依存する判定はここに書かない。** 業務エリア設定は取得前に
 * 全エリアへフォールバックするため、 純関数からは正しい候補集合が見えない。
 * stale code の除去と「全件選択は空へ畳む」正規化は呼び出し側 (`useFilterState`) の
 * `normalizeSelectedAreaCodes` が持つ。
 */
export const supplyGapFiltersCodec = defineFilterCodec<GapFilterState>({
  defaults: defaultSupplyGapFilters,
  fields: {
    rangeType: guarded(isRangeType),
    dayDate: ymd,
    weekStart: ymd,
    weekDur: oneOf<number>(WEEK_DUR_OPTIONS),
    monthStart: ymd,
    monthDur: oneOf<number>(MONTH_DUR_OPTIONS),
    representativeValue: oneOf(REP_VALUE_OPTIONS.map((option) => option.value)),
    breakdownMode: guarded(isBreakdownMode),
    selectedAreaCodes: listOf(oneOf<AreaCode>(AREA_CODE_OPTIONS)),
  },
})
